import { gql, type TypedDocumentNode } from '@apollo/client';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconChevronLeft } from '@sorare/core/src/atoms/icons/system/iconChevronLeft.ts';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Dialog } from '@sorare/core/src/components/dialog';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isTypes } from '@sorare/core/src/lib/gql';
import { useCommonGameSet } from '@sorare/core/src/set';
import { SeriesBalance } from '@sorare/marketplace/src/components/commonCard/BuyPacks/SeriesBalance';

import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import { SetCollectionTaskDetails } from './SetCollectionTaskDetails/index.tsx';
import type {
  SetCollectionTaskDialog as SetCollectionTaskDialogQuery,
  SetCollectionTaskDialogVariables,
} from './__generated__/index.graphql.ts';

const BackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  & > * {
    width: 100%;
    height: 100%;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(14, 14, 14, 0.5) 0%,
      var(--c-black) 100%
    );
    mix-blend-mode: color;
    pointer-events: none;
  }
`;

const ScrollableContent = styled(Vertical).attrs({ gap: 3 })`
  position: relative;
  z-index: 1;
  padding: var(--double-unit);
  padding-top: var(--triple-unit);
`;

const HeaderRow = styled(SBHorizontal)`
  padding: var(--half-unit) 0;
  align-items: center;
`;

const SET_COLLECTION_TASK_DIALOG_QUERY = gql`
  query SetCollectionTaskDialog($taskId: ID!) {
    currentUser {
      slug
      task(id: $taskId, bypassRebuildCollectionTask: true) {
        id
        ... on CardsCountTargetTask {
          id
          competitions {
            slug
            displayName
          }
        }
        ... on CompleteCollectionsCountTargetTask {
          id
          competitions {
            slug
            displayName
          }
        }
        ...SetCollectionTaskDetails_taskInterface
      }
    }
  }
  ${SetCollectionTaskDetails.fragments.taskInterface}
` as TypedDocumentNode<
  SetCollectionTaskDialogQuery,
  SetCollectionTaskDialogVariables
>;
type Props = {
  open: boolean;
  onClose: () => void;
  taskId: string;
};

export const SetCollectionTaskDialog = ({ open, onClose, taskId }: Props) => {
  const commonGameSet = useCommonGameSet();
  const { data } = useQuery(SET_COLLECTION_TASK_DIALOG_QUERY, {
    variables: {
      taskId: idFromObject(taskId),
    },
    skip: !open,
  });
  const currentUser = data?.currentUser;
  const task = currentUser?.task;
  if (
    !isTypes(task, [
      'CardsCountTargetTask',
      'CompleteCollectionsCountTargetTask',
    ])
  ) {
    return null;
  }

  const competitionColor = getTaskCompetitionColor(
    task,
    'var(--c-inventory-yellow)'
  );

  return (
    <Dialog open={open} onClose={onClose} hideHeader maxWidth="sm">
      <BackgroundWrapper>
        <TextureBackground
          background="wave1"
          mode="dark"
          color={competitionColor}
        />
      </BackgroundWrapper>
      <ScrollableContent>
        <HeaderRow>
          <IconButton
            size="small"
            onClick={onClose}
            icon={iconChevronLeft}
            color="quaternary"
          />
          <SeriesBalance cardSetSlugs={[commonGameSet.slug]} />
        </HeaderRow>
        <SetCollectionTaskDetails task={task} />
      </ScrollableContent>
    </Dialog>
  );
};
